import { Component, ReactNode, ErrorInfo } from 'react';

// Mendefinisikan tipe data untuk props yang diterima oleh ErrorBoundary.
// - children: Komponen anak yang akan "dilindungi" oleh Error Boundary.
// - fallback: Komponen UI yang akan ditampilkan jika terjadi error.
interface Props {
    children: ReactNode;
    fallback?: ReactNode;
}

// Mendefinisikan tipe data untuk state internal dari ErrorBoundary.
// - hasError: Sebuah boolean untuk menandai apakah error telah terjadi.
interface State {
    hasError: boolean;
}

/**
 * ErrorBoundary adalah komponen kelas React yang menangkap error JavaScript
 * di mana saja di dalam pohon komponen anaknya, mencatat error tersebut,
 * dan menampilkan UI fallback alih-alih pohon komponen yang crash.
 */
class ErrorBoundary extends Component<Props, State> {
    /**
     * State awal komponen. Secara default, tidak ada error.
     */
    public state: State = {
        hasError: false,
    };

    /**
     * Lifecycle method ini dipanggil setelah error dilemparkan oleh komponen anak.
     * Method ini digunakan untuk memperbarui state, yang akan memicu render ulang
     * untuk menampilkan UI fallback.
     *
     * @param _ - Error yang dilemparkan. Tidak digunakan di sini, tapi merupakan bagian dari signature.
     * @returns Sebuah objek state baru.
     */
    public static getDerivedStateFromError(_: Error): State {
        // Memperbarui state sehingga render berikutnya akan menampilkan UI fallback.
        return { hasError: true };
    }

    /**
     * Lifecycle method ini dipanggil setelah error dilemparkan oleh komponen anak.
     * Method ini adalah tempat yang tepat untuk melakukan side effects, seperti
     * mengirim log error ke layanan eksternal (misalnya Sentry, LogRocket, dll.).
     *
     * @param error - Objek Error yang dilemparkan.
     * @param errorInfo - Objek dengan `componentStack` yang berisi informasi
     *                    tentang komponen mana yang menyebabkan error.
     */
    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        // Di sini Anda bisa mencatat error ke layanan pelaporan error.
        // Untuk contoh ini, kita hanya akan mencetaknya ke konsol.
        console.error("Uncaught error in ErrorBoundary:", error, errorInfo);
    }

    /**
     * Method render utama.
     * Memeriksa state `hasError`. Jika true, tampilkan UI fallback.
     * Jika false, tampilkan komponen anak seperti biasa.
     */
    public render() {
        if (this.state.hasError) {
            // Jika prop 'fallback' disediakan, render itu.
            // Jika tidak, render sebuah UI fallback default yang informatif.
            return (
                this.props.fallback || (
                    <div style={{ padding: '20px', textAlign: 'center', border: '1px solid #ff000033', borderRadius: '8px', backgroundColor: '#fff5f5' }}>
                        <h2 style={{ color: '#cc0000' }}>Something went wrong.</h2>
                        <p>An error occurred in this section of the application.</p>
                        <button
                            onClick={() => window.location.reload()}
                            style={{
                                padding: '8px 16px',
                                border: 'none',
                                backgroundColor: '#ff4d4d',
                                color: 'white',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            Refresh Page
                        </button>
                    </div>
                )
            );
        }

        // Jika tidak ada error, render komponen anak seperti biasa.
        return this.props.children;
    }
}

export default ErrorBoundary;